import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import useSEO from 'hooks/useSEO'
import LPNavigation from '../landing/sections/LPNavigation'
import CTABanner from '../landing/sections/CTABanner'
import LPFooter from '../landing/sections/LPFooter'

// ── Glossary terms ────────────────────────────────────────────────────────────
const TERMS = [
  // A
  {
    letter: 'A',
    term: 'Annual Dividend Yield',
    definition: 'The total dividends paid per share over a year, divided by the current share price, expressed as a percentage. A stock priced at $50 that pays $2/year in dividends has a 4% annual yield. Yield changes as the share price moves — a falling price raises the yield.',
    link: { label: 'Track yield in SDT', href: '/register' },
  },
  {
    letter: 'A',
    term: 'Assignment',
    definition: 'When an options contract is exercised against you. If you sold a covered call and the stock rises above the strike price, you may be assigned — meaning your shares are sold at the strike price. If you sold a cash-secured put and the stock falls below the strike, you are assigned and must buy 100 shares at the strike.',
    link: { label: 'Track assignments', href: '/covered-call-tracker' },
  },
  // C
  {
    letter: 'C',
    term: 'Cash-Secured Put (CSP)',
    definition: 'An options strategy where you sell a put option and set aside enough cash to buy 100 shares if assigned. You collect a premium upfront. If the stock stays above the strike price at expiry, the put expires worthless and you keep the premium. If it falls below, you buy the shares at the strike — often a stock you wanted to own anyway.',
    link: { label: 'Track CSPs in SDT', href: '/covered-call-tracker' },
  },
  {
    letter: 'C',
    term: 'Collateral',
    definition: 'Cash or shares set aside to back an options position. For a covered call, the collateral is the 100 shares you own. For a cash-secured put, the collateral is the cash required to buy 100 shares at the strike price. Return on collateral (ROC) measures how efficiently that capital is working.',
  },
  {
    letter: 'C',
    term: 'Covered Call',
    definition: 'An options strategy where you own at least 100 shares of a stock and sell someone the right to buy those shares at a set price (the strike) by a set date. You collect a cash premium upfront. If the stock stays below the strike, you keep both the shares and the premium. If it rises above the strike, your shares may be called away — but you still keep the premium.',
    link: { label: 'Track covered calls', href: '/covered-call-tracker' },
  },
  // D
  {
    letter: 'D',
    term: 'Dividend',
    definition: 'A cash payment made by a company to its shareholders, typically from profits. Dividends are usually paid quarterly (in the US) or monthly (common for REITs and income ETFs). They are expressed as a per-share amount — for example, $0.50 per share per quarter.',
  },
  {
    letter: 'D',
    term: 'Dividend Aristocrat',
    definition: 'An S&P 500 company that has increased its dividend every year for at least 25 consecutive years. Aristocrats are considered the gold standard of dividend reliability. Examples include Coca-Cola (KO), Johnson & Johnson (JNJ), and Realty Income (O). Companies with 50+ years of increases are called Dividend Kings.',
  },
  {
    letter: 'D',
    term: 'Dividend Calendar',
    definition: 'A visual schedule showing upcoming ex-dividend dates and payment dates for stocks in your portfolio. A dividend calendar helps investors plan cash flow, avoid missing ex-dates, and track which holdings will pay next.',
    link: { label: 'View dividend calendar', href: '/register' },
  },
  {
    letter: 'D',
    term: 'Dividend Cut',
    definition: 'When a company reduces its dividend payment. A cut is one of the most significant negative signals for income investors — it often causes a sharp drop in the stock price and means less income from that holding. Monitoring dividend safety helps anticipate potential cuts before they happen.',
  },
  {
    letter: 'D',
    term: 'Dividend Growth Investing',
    definition: 'An investment strategy focused on buying companies that consistently raise their dividends over time. The goal is to own stocks whose income grows faster than inflation, so your purchasing power increases year over year. DRIP compounding accelerates this strategy significantly.',
    link: { label: 'Project your growth', href: '/drip-calculator' },
  },
  {
    letter: 'D',
    term: 'Dividend King',
    definition: 'A company that has raised its dividend every year for 50 or more consecutive years. Dividend Kings are a subset of Dividend Aristocrats and represent the most reliable dividend payers in the market. Examples include Procter & Gamble (PG), Coca-Cola (KO), and 3M (MMM).',
  },
  {
    letter: 'D',
    term: 'Dividend Reinvestment Plan (DRIP)',
    definition: 'A program where dividend payments are automatically used to purchase more shares of the same stock instead of being paid out as cash. DRIP creates a compounding snowball effect — more shares generate more dividends, which buy even more shares. Over decades, DRIP dramatically increases total income and portfolio value.',
    link: { label: 'Try the DRIP calculator', href: '/drip-calculator' },
  },
  {
    letter: 'D',
    term: 'Dividend Yield',
    definition: 'The annual dividend per share divided by the current share price, expressed as a percentage. Yield is a snapshot in time — it changes as the share price moves. A 5% yield on a $40 stock means it pays $2/year in dividends. Compare trailing yield (based on past payments) to forward yield (based on projected payments).',
  },
  // E
  {
    letter: 'E',
    term: 'Ex-Dividend Date',
    definition: 'The cut-off date to qualify for an upcoming dividend. You must own the shares before the ex-dividend date to receive the payment. If you buy on or after the ex-date, the previous owner receives the dividend, not you. The stock price often drops by roughly the dividend amount on the ex-date.',
    link: { label: 'Track ex-dates', href: '/register' },
  },
  {
    letter: 'E',
    term: 'Expiration Date',
    definition: 'The date on which an options contract expires and is settled. For a covered call or cash-secured put, if the option expires out-of-the-money (below strike for calls, above strike for puts), it expires worthless and you keep the full premium with no further obligation.',
  },
  // F
  {
    letter: 'F',
    term: 'Forward Dividend Yield',
    definition: 'An estimate of the next 12 months of dividends divided by the current share price. Calculated by multiplying the most recent quarterly dividend by 4 (or monthly by 12). More useful than trailing yield for fast-growing dividend payers who have recently raised their payout.',
  },
  // I
  {
    letter: 'I',
    term: 'In-The-Money (ITM)',
    definition: 'An options term describing when exercise would be profitable. A covered call is in-the-money when the stock price is above the strike price. A cash-secured put is in-the-money when the stock price is below the strike price. ITM options carry higher assignment risk.',
  },
  // O
  {
    letter: 'O',
    term: 'Options Premium',
    definition: 'The cash you receive when you sell an options contract (covered call or cash-secured put). Premium is yours to keep regardless of what happens to the contract — whether it expires worthless, is closed early, or results in assignment. Premium per contract × 100 (shares per contract) = total cash collected.',
    link: { label: 'Track premium income', href: '/covered-call-tracker' },
  },
  {
    letter: 'O',
    term: 'Out-of-the-Money (OTM)',
    definition: 'A covered call is out-of-the-money when the stock price is below the strike price — it will likely expire worthless, letting you keep the shares and the full premium. Most income-focused covered call sellers prefer to sell OTM calls to reduce the risk of having shares called away.',
  },
  // P
  {
    letter: 'P',
    term: 'Payment Date',
    definition: 'The date on which a declared dividend is actually paid to shareholders who were on record as of the record date. The payment date is typically 2–4 weeks after the ex-dividend date. This is when the cash hits your brokerage account.',
  },
  {
    letter: 'P',
    term: 'Payout Ratio',
    definition: 'The percentage of a company\'s earnings paid out as dividends. A 60% payout ratio means the company pays out 60 cents of every dollar it earns. A lower ratio suggests the dividend is more sustainable and has room to grow. Ratios above 90–100% can signal an unsustainable dividend — a cut may be coming.',
  },
  // Q
  {
    letter: 'Q',
    term: 'Qualified Dividend',
    definition: 'A dividend that meets IRS requirements to be taxed at the lower long-term capital gains rate (0%, 15%, or 20%) rather than the ordinary income rate. To qualify, you must hold the stock for more than 60 days around the ex-dividend date. Most dividends from US corporations are qualified; REITs and foreign stocks often are not.',
  },
  // R
  {
    letter: 'R',
    term: 'Record Date',
    definition: 'The date on which a company checks its books to determine which shareholders qualify for an upcoming dividend. You must be a shareholder of record on this date to receive the dividend. Because of T+1 stock settlement, the ex-dividend date is the day before the record date.',
  },
  {
    letter: 'R',
    term: 'Return on Collateral (ROC)',
    definition: 'The percentage return earned on an options trade relative to the collateral tied up. Calculated as (premium collected ÷ strike price) × 100. For example, collecting $80 premium on a $4,000 CSP position is a 2% ROC for that trade cycle. Annualised ROC helps compare options trades of different durations.',
    link: { label: 'See ROC in SDT', href: '/covered-call-tracker' },
  },
  // S
  {
    letter: 'S',
    term: 'Strike Price',
    definition: 'The fixed price at which an options contract can be exercised. For a covered call, the strike is the price at which your shares could be called away. For a cash-secured put, the strike is the price at which you would be obligated to buy 100 shares. Sellers typically choose a strike that balances premium income with acceptable risk.',
  },
  // T
  {
    letter: 'T',
    term: 'Total Return',
    definition: 'The complete return on an investment including both price appreciation and dividend income. A stock that rises 5% and pays a 3% dividend has a total return of approximately 8%. Income investors often focus on dividend return, but total return captures the full picture of an investment\'s performance.',
  },
  // W
  {
    letter: 'W',
    term: 'Wheel Strategy',
    definition: 'A repeating options income cycle that combines cash-secured puts and covered calls: (1) Sell a CSP to collect premium — if assigned, buy shares at the strike. (2) Sell covered calls on the assigned shares to collect more premium. (3) If shares are called away, repeat from step 1. Dividend investors use the wheel to layer options income on top of dividend payments.',
    link: { label: 'Track the wheel in SDT', href: '/covered-call-tracker' },
  },
  // Y
  {
    letter: 'Y',
    term: 'Yield on Cost (YOC)',
    definition: 'The annual dividend per share divided by your original cost basis — not the current price. YOC grows over time as companies raise their dividends. A stock bought at $20 that now pays $1.20/year has a 6% YOC even if the current share price and yield have changed significantly. Long-term dividend growth investors track YOC to measure the true income return on their original investment.',
    link: { label: 'Track YOC in SDT', href: '/register' },
  },
]

// ── Group by letter ───────────────────────────────────────────────────────────
const LETTERS = [...new Set(TERMS.map(t => t.letter))].sort()
const byLetter = LETTERS.reduce((acc, l) => {
  acc[l] = TERMS.filter(t => t.letter === l)
  return acc
}, {})

export default function GlossaryPage() {
  useSEO({
    title: 'Dividend Investing Glossary — Key Terms Explained',
    description: 'Plain-English definitions of dividend investing terms: DRIP, ex-dividend date, yield on cost, covered calls, the wheel strategy, and more.',
    canonical: '/glossary',
  })

  // Inject DefinedTermSet + FAQPage JSON-LD
  useEffect(() => {
    const id = 'glossary-jsonld'
    if (document.getElementById(id)) return
    const s = document.createElement('script')
    s.id = id
    s.type = 'application/ld+json'
    s.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: TERMS.map(t => ({
        '@type': 'Question',
        name: `What is ${t.term}?`,
        acceptedAnswer: { '@type': 'Answer', text: t.definition },
      })),
    })
    document.head.appendChild(s)
    return () => document.getElementById(id)?.remove()
  }, [])

  const [search, setSearch] = useState('')
  const sectionRefs = useRef({})

  const filtered = search.trim()
    ? TERMS.filter(t =>
        t.term.toLowerCase().includes(search.toLowerCase()) ||
        t.definition.toLowerCase().includes(search.toLowerCase())
      )
    : null

  const scrollTo = (letter) => {
    sectionRefs.current[letter]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="min-h-screen bg-white">
      <LPNavigation />

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 bg-gradient-to-b from-[#F0FDF4] to-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4">Reference</p>
          <h1 className="font-display text-5xl lg:text-6xl text-[#0F172A] leading-tight">
            Dividend Investing<br className="hidden sm:block" /> Glossary
          </h1>
          <p className="mt-4 text-lg text-[#475569] max-w-xl mx-auto">
            Plain-English definitions of every term you'll encounter tracking dividends, running covered calls, and building passive income.
          </p>
          {/* Search */}
          <div className="mt-8 max-w-md mx-auto relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search terms…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border border-[#E2E8F0] rounded-full text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]/30 focus:border-[#1B5E20] bg-white shadow-sm"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]">✕</button>
            )}
          </div>
        </div>
      </section>

      {/* Letter nav */}
      {!filtered && (
        <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E2E8F0] px-4 py-2">
          <div className="max-w-4xl mx-auto flex gap-1 flex-wrap justify-center">
            {LETTERS.map(l => (
              <button
                key={l}
                onClick={() => scrollTo(l)}
                className="w-8 h-8 rounded-md text-sm font-semibold text-[#475569] hover:bg-[#F0FDF4] hover:text-[#1B5E20] transition-colors"
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Terms */}
      <section className="px-4 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Search results */}
          {filtered && (
            filtered.length === 0 ? (
              <p className="text-center text-[#94A3B8] py-16">No terms found for "{search}"</p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-[#64748B] mb-6">{filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"</p>
                {filtered.map(term => <TermCard key={term.term} term={term} />)}
              </div>
            )
          )}

          {/* Alphabetical sections */}
          {!filtered && LETTERS.map(letter => (
            <div
              key={letter}
              ref={el => { sectionRefs.current[letter] = el }}
              className="mb-12 scroll-mt-32"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#1B5E20] flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-base">{letter}</span>
                </div>
                <div className="flex-1 h-px bg-[#E2E8F0]" />
              </div>
              <div className="space-y-4">
                {byLetter[letter].map(term => <TermCard key={term.term} term={term} />)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner
        headline="Put these terms into practice"
        subtext="Track your dividends, DRIP projections, and options income — free forever."
        ctaText="Start Tracking Free"
        microcopy="Free plan forever · No credit card required"
      />
      <LPFooter />
    </div>
  )
}

function TermCard({ term }) {
  return (
    <div id={`term-${term.term.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
      className="bg-white border border-[#E2E8F0] rounded-xl p-6 hover:border-[#DCFCE7] hover:shadow-sm transition-all duration-200"
    >
      <h2 className="text-lg font-semibold text-[#0F172A] mb-2">{term.term}</h2>
      <p className="text-sm text-[#475569] leading-relaxed">{term.definition}</p>
      {term.link && (
        <Link
          to={term.link.href}
          className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-[#1B5E20] hover:underline"
        >
          {term.link.label} →
        </Link>
      )}
    </div>
  )
}
